import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Gauge, Stack, Wrench, SealCheck, FileText, ShieldCheck, Users, QrCode,
  Brain, SignOut, List as ListIcon, X, Headset, Siren, Cube, Warehouse, Images, CloudArrowUp, Folders,
  FileText as ContractIcon, Buildings, UserCircle, Scales,
  CalendarBlank, Kanban, ChartBar, BellSimple, MapTrifold, ClipboardText, ListChecks, TreeStructure,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";
import { SyncIndicator } from "@/components/SyncIndicator";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, roles: null },
  { to: "/kennzahlen", label: "Kennzahlen", icon: ShieldCheck, roles: ["admin", "buero", "innendienst"] },
  { to: "/plantafel", label: "Plantafel", icon: Kanban, roles: ["admin", "buero", "innendienst"] },
  { to: "/kalender", label: "Kalender", icon: CalendarBlank, roles: null },
  { to: "/analytics", label: "Analytics", icon: ChartBar, roles: ["admin", "buero", "innendienst"] },
  { to: "/benachrichtigungen", label: "Benachrichtigungen", icon: BellSimple, roles: null },
  { to: "/standorte", label: "Standorte", icon: MapTrifold, roles: null },
  { to: "/wartungsauftraege", label: "Wartungsaufträge", icon: ClipboardText, roles: null },
  { to: "/cms", label: "CMS-Engine · Abnahme", icon: TreeStructure, roles: ["admin", "buero"] },
  { to: "/cms-editor", label: "CMS-Core-Editor", icon: TreeStructure, roles: ["admin", "buero", "innendienst"] },
  { to: "/digital-twin", label: "Digital Twin", icon: Gauge, roles: ["admin", "buero", "innendienst"] },
  { to: "/stammdaten", label: "Stammdaten · Kataloge", icon: Cube, roles: ["admin", "buero"] },
  { to: "/anlagen", label: "Anlagen", icon: Stack, roles: null },
  { to: "/bestandsaufnahme", label: "Bestandsaufnahme", icon: QrCode, roles: null },
  { to: "/service", label: "Service", icon: Folders, roles: null },
  { to: "/reparaturen", label: "Reparaturen", icon: Headset, roles: null },
  { to: "/notdienst", label: "Notdienst", icon: Siren, roles: null },
  { to: "/material", label: "Material", icon: Cube, roles: null },
  { to: "/lager", label: "Lager", icon: Warehouse, roles: null },
  { to: "/vertraege", label: "Verträge", icon: ContractIcon, roles: null },
  { to: "/kunden", label: "CRM · Kunden", icon: Buildings, roles: null },
  { to: "/kundenportal", label: "Kundenportal", icon: UserCircle, roles: null },
  { to: "/regelwerk", label: "Regelwerk", icon: Scales, roles: ["admin", "buero", "innendienst"] },
  { to: "/medien", label: "Medien", icon: Images, roles: null },
  { to: "/ki-analyse", label: "KI-Analyse", icon: Brain, roles: null },
  { to: "/dokumente", label: "Dokumente", icon: FileText, roles: null },
  { to: "/sync", label: "Sync", icon: CloudArrowUp, roles: null },
  { to: "/audit", label: "Audit-Trail", icon: ShieldCheck, roles: ["admin", "buero", "innendienst"] },
  { to: "/verwaltung", label: "Verwaltung", icon: Users, roles: ["admin"] },
];

function QrScanDialog() {
  const [code, setCode] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const resolve = async () => {
    try {
      const { data } = await api.get(`/qr/resolve?code=${encodeURIComponent(code.trim())}`);
      const name = data.type === "component" ? data.component?.bezeichnung : data.asset?.name;
      toast.success("QR-Code erfolgreich aufgelöst", { description: name });
      setOpen(false); setCode("");
      const assetId = data.asset?.id || data.component?.asset_id;
      navigate(`/anlage/${assetId}`);
    } catch (e) {
      toast.error("QR-Code nicht gefunden", { description: formatApiError(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="qr-scan-button" className="gap-2">
          <QrCode size={18} weight="bold" /> <span className="hidden sm:inline">QR-Scan</span>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="qr-scan-dialog">
        <DialogHeader><DialogTitle className="font-heading">Anlage per QR-Code öffnen</DialogTitle></DialogHeader>
        <DialogDescription>Scannen Sie die Plakette oder geben Sie den QR-Code der Anlage ein.</DialogDescription>
        <Input data-testid="qr-code-input" autoFocus placeholder="z.B. CSP-AST-1A2B3C4D / CSP-CMP-…"
          value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && resolve()} className="h-12 font-mono" />
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" data-testid="qr-camera-scan-button" onClick={() => { setOpen(false); navigate("/scan"); }}>Mit Kamera scannen</Button>
          <Button data-testid="qr-resolve-button" onClick={resolve} disabled={!code.trim()}>Öffnen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1 p-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onClick}
          data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}>
          <n.icon size={20} weight="duotone" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl sm:px-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open">
            <ListIcon size={22} />
          </Button>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading font-black">C</div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-black tracking-tight">CELENTANO</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Service Platform</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <GlobalSearch />
          <QrScanDialog />
          <SyncIndicator />
          <NotificationBell />
          <ThemeToggle />
          <div className="hidden text-right sm:block">
            <div className="text-sm font-semibold leading-tight">{user?.name}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{user?.role}</div>
          </div>
          <Button variant="ghost" size="icon" data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }} aria-label="Abmelden">
            <SignOut size={20} weight="bold" />
          </Button>
        </div>
      </header>

      <div className="flex">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-60 shrink-0 overflow-y-auto border-r border-border lg:block">
          <NavItems />
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 bg-card shadow-xl">
              <div className="flex h-16 items-center justify-between border-b border-border px-4">
                <span className="font-heading font-black">Menü</span>
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}><X size={20} /></Button>
              </div>
              <NavItems onClick={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
